import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  fetchCandidateComments,
  fetchCandidateFiles,
  fetchCandidateActivity,
  resetChat,
} from "../Redux/ActionCreators/chatAction";
import RecruiterChat from "../Components/Chat/RecruiterChat";

const TITLES = {
  messages: "Messages",
  activity: "Activity",
  notes: "Notes",
  files: "Files",
};

const TAB_ICONS = {
  messages: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  activity: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  notes: (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </>
  ),
  files: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>
  ),
};

const TAB_ORDER = ["messages", "activity", "notes", "files"];

const EMPTY_STATES = {
  activity: { title: "No activity yet", sub: "Candidate interactions will appear here" },
  notes: { title: "No notes yet", sub: "Internal notes about this candidate will appear here" },
  files: { title: "No files yet", sub: "Resume uploads, chat attachments and manual uploads" },
};

const FILE_SOURCE_LABELS = { resume_upload: "Resume", chat_in_app: "Chat", chat_messenger: "Chat", manual: "Uploaded" };
const FILE_SOURCE_BG = { resume_upload: "#dbeafe", chat_in_app: "#dcfce7", chat_messenger: "#dcfce7", manual: "#fce7f3" };
const FILE_SOURCE_COLOR = { resume_upload: "#1d4ed8", chat_in_app: "#15803d", chat_messenger: "#15803d", manual: "#be185d" };

const formatDateTime = (d) =>
  d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const formatSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const downloadFile = async (url, name) => {
  try {
    const r = await fetch(url);
    const blob = await r.blob();
    const bUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = bUrl;
    a.download = name || "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(bUrl);
  } catch {
    window.open(url, "_blank");
  }
};

const renderChangeValue = (valueType, display) => {
  if (display == null || display === "") return <span style={{ color: "#94a3b8", fontStyle: "italic" }}>—</span>;
  switch (valueType) {
    case "status":
      return (
        <span style={{ display: "inline-block", padding: "1px 7px", borderRadius: 10, background: "#e8f0fe", color: "#1c2b4a", fontSize: 10, fontWeight: 600, letterSpacing: 0.2 }}>
          {display}
        </span>
      );
    case "boolean": {
      const yes = display === true || display === "true";
      return (
        <span style={{ display: "inline-block", padding: "1px 7px", borderRadius: 10, background: yes ? "#dcfce7" : "#fee2e2", color: yes ? "#166534" : "#991b1b", fontSize: 10, fontWeight: 600 }}>
          {yes ? "Yes" : "No"}
        </span>
      );
    }
    case "number":
      return <span style={{ fontWeight: 600, color: "#1c2b4a", fontSize: 11 }}>{display}</span>;
    case "url":
      return (
        <a href={display} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#2563eb", textDecoration: "underline", wordBreak: "break-all" }}>
          {display.length > 40 ? display.slice(0, 37) + "…" : display}
        </a>
      );
    default:
      return <span style={{ fontSize: 11, color: "#1e293b" }}>{String(display)}</span>;
  }
};

const Spinner = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
    <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }} />
  </div>
);

const Empty = ({ type }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "32px 0", color: "#8A9199", fontSize: 13 }}>
    <span>{EMPTY_STATES[type].title}</span>
    <span style={{ fontSize: 11 }}>{EMPTY_STATES[type].sub}</span>
  </div>
);

const ActivityList = ({ items }) => (
  <div style={{ display: "flex", flexDirection: "column" }}>
    {items.map((item, i) => {
      const changes = item?.meta_data?.data?.changes;
      return (
        <div key={item.id || i} style={{ display: "flex", gap: 10, paddingBottom: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
            <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#1c2b4a", marginTop: 3 }} />
            {i < items.length - 1 && <div style={{ width: 1, flex: 1, background: "#e4e9f0", marginTop: 4 }} />}
          </div>
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontSize: 13, color: "#1e293b", lineHeight: 1.4 }}>
              {item.title || item.description || item.event_type || "Activity"}
            </div>
            {Array.isArray(changes) && changes.length > 0 && (
              <div style={{ marginTop: 5, display: "flex", flexDirection: "column", gap: 4 }}>
                {changes.map((change, ci) => (
                  <div key={ci} style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                    {change.label && <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{change.label}:</span>}
                    {renderChangeValue(change.value_type, change.old_display)}
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>→</span>
                    {renderChangeValue(change.value_type, change.new_display)}
                  </div>
                ))}
              </div>
            )}
            {item.actor_name && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>by {item.actor_name}</div>}
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{formatDateTime(item.created_at)}</div>
          </div>
        </div>
      );
    })}
  </div>
);

// Read-only: the composer / edit / delete controls from the profile's Notes tab are intentionally omitted.
const NotesList = ({ items }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {items.map((note) => (
      <div key={note.id} style={{ border: "1px solid #e4e9f0", borderRadius: 10, padding: "10px 12px", background: "#fff", textAlign: "left" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{note.author_name || "Recruiter"}</span>
            {note.author_email && <div style={{ fontSize: 11, color: "#94a3b8" }}>{note.author_email}</div>}
          </div>
          <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0, marginLeft: 8 }}>{formatDateTime(note.created_at)}</span>
        </div>
        {note.body_text && (
          <p style={{ fontSize: 13, color: "#1e293b", margin: "0 0 8px", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", textAlign: "left" }}>
            {note.body_text}
          </p>
        )}
        {(note.attachments || []).map((att, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e4e9f0", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.file_name || "File"}</span>
            {att.file_url && (
              <button onClick={() => window.open(att.file_url, "_blank")} style={{ fontSize: 11, color: "#1c2b4a", background: "none", border: "none", cursor: "pointer", padding: 0 }}>View</button>
            )}
          </div>
        ))}
      </div>
    ))}
  </div>
);

const FilesList = ({ items }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {items.map((file) => {
      const size = formatSize(file.file_size_bytes ?? file.file_size ?? null);
      return (
        <div key={file.id} style={{ border: "1px solid #e4e9f0", borderRadius: 10, padding: "10px 12px", background: "#fff" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 999, background: FILE_SOURCE_BG[file.source] || "#f1f5f9", color: FILE_SOURCE_COLOR[file.source] || "#64748b" }}>
              {FILE_SOURCE_LABELS[file.source] || file.source || "File"}
            </span>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              {file.created_at ? new Date(file.created_at).toLocaleDateString([], { month: "short", day: "numeric" }) : ""}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>📄</span>
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.file_name || "File"}</div>
              {size && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{size}</div>}
            </div>
            {file.file_url && (
              <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                <button onClick={() => window.open(file.file_url, "_blank")} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #e4e9f0", background: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#1e293b" }}>View</button>
                <button onClick={() => downloadFile(file.file_url, file.file_name)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #e4e9f0", background: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#1e293b" }}>↓</button>
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const FETCHERS = {
  notes: fetchCandidateComments,
  files: fetchCandidateFiles,
  activity: fetchCandidateActivity,
};

/**
 * Popup showing one candidate detail section (messages / activity / notes / files).
 * Notes are read-only. Sending a message in the Messages popup calls `onMessageSent`,
 * which the parent uses to navigate to the candidate profile with the messages panel open.
 * `onSwitch(type)` lets the header icons jump between sections without closing the popup.
 */
const CandidateDetailsModal = ({ type, jobId, candidate, onClose, onMessageSent, onSwitch }) => {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatReady, setChatReady] = useState(false);
  const candidateId = candidate?.id;

  useEffect(() => {
    if (!type || !candidateId) return undefined;
    let cancelled = false;
    if (type === "messages") {
      // Clear any conversation left over from another candidate before RecruiterChat mounts.
      dispatch(resetChat());
      setChatReady(true);
      return () => {
        setChatReady(false);
        dispatch(resetChat());
      };
    }
    setItems([]);
    setLoading(true);
    dispatch(FETCHERS[type](jobId, candidateId)).then((res) => {
      if (cancelled) return;
      const data = res?.data?.items || res?.data || [];
      setItems(Array.isArray(data) ? data : []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [type, jobId, candidateId, dispatch]);

  if (!type || !candidate) return null;

  const isMessages = type === "messages";

  return (
    <div className="jv2-modal-overlay" onClick={onClose}>
      <div className="jv2-modal" style={{ maxWidth: isMessages ? 520 : 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="jv2-modal-head">
          <div className="cand-details-head-left">
            <h3>{TITLES[type]}{candidate.name ? ` · ${candidate.name}` : ""}</h3>
            <div className="cand-details-tabs">
              {TAB_ORDER.map((key) => (
                <button
                  key={key}
                  type="button"
                  className="cand-details-tab-btn"
                  title={TITLES[key]}
                  disabled={key === type}
                  onClick={() => onSwitch?.(key)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {TAB_ICONS[key]}
                  </svg>
                </button>
              ))}
            </div>
          </div>
          <button className="jv2-modal-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        </div>
        {isMessages ? (
          <div className="cand-details-chat" style={{ height: "min(560px, 72vh)", display: "flex", flexDirection: "column" }}>
            {chatReady && (
              <RecruiterChat
                jobCandidateId={candidateId}
                candidateName={candidate.name}
                convLoading={false}
                isVisible
                onMessageSent={onMessageSent}
              />
            )}
          </div>
        ) : (
          <div className="jv2-modal-body" style={{ maxHeight: "65vh", overflowY: "auto", textAlign: "left" }}>
            {loading ? <Spinner /> : items.length === 0 ? <Empty type={type} /> : (
              type === "activity" ? <ActivityList items={items} />
                : type === "notes" ? <NotesList items={items} />
                : <FilesList items={items} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateDetailsModal;
